import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import CloseButton from '../shared/CloseButton';
import LoyaltyCard from '../loyalty-v2/LoyaltyCard';
import useWallet from './useWallet';
import './Wallet.css';
import './LoyaltyWallet.css';

const StampIcon = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="9" cy="9" r="4.2" /><circle cx="16.5" cy="15.5" r="3.4" strokeDasharray="2.4 2.6" /><path d="M14.5 5.5 18.5 9.5M18.5 5.5l-4 4" />
  </svg>
);
const BackArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24" aria-hidden>
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);
const SearchIcon = () => (
  <svg className="loy-search-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
    <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// LoyaltyCard takes a px width and scales everything off it, so we measure the
// available width and feed it in — keeping cards crisp at any viewport.
const ResponsiveLoyaltyCard = (props) => {
  const ref = useRef(null);
  const [w, setW] = useState(360);
  useEffect(() => {
    const measure = () => { if (ref.current) setW(ref.current.offsetWidth); };
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined' && ref.current) {
      ro = new ResizeObserver(measure);
      ro.observe(ref.current);
    } else {
      window.addEventListener('resize', measure);
    }
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, []);
  const portrait = props.design && props.design.orientation === 'portrait';
  // Portrait cards are tall; cap their width so they don't dominate the column.
  const width = portrait ? Math.min(w, 260) : w;
  return (
    <div ref={ref} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <LoyaltyCard {...props} width={width} />
    </div>
  );
};

const isReady = (item) => {
  const s = item.scheme || {};
  const st = item.state || {};
  // A reward is claimable when the backend says so; fall back to the raw
  // threshold for older payloads without rewardsAvailable.
  if ((st.rewardsAvailable || 0) > 0) return true;
  return s.mode === 'points'
    ? (st.balance || 0) >= (s.pointsTarget || 500)
    : (st.filled || 0) >= (s.stampsRequired || 6);
};

const progressText = (item) => {
  const s = item.scheme || {};
  const st = item.state || {};
  if (isReady(item)) return 'Reward ready';
  if (s.mode === 'points') {
    return `${(st.balance || 0).toLocaleString()} / ${(s.pointsTarget || 500).toLocaleString()} points`;
  }
  return `${st.filled || 0} of ${s.stampsRequired || 6} stamps`;
};

const LoyaltyDetail = ({ item, onClose, onBook }) => {
  const rows = item.scheme.mode === 'points'
    ? [
      { k: 'How you earn', v: `${item.scheme.pointsPerPound} point${item.scheme.pointsPerPound > 1 ? 's' : ''} per £1 spent` },
      { k: 'Your points', v: (item.state.balance || 0).toLocaleString() },
      { k: 'Reward', v: `${item.scheme.pointsLabel} at ${(item.scheme.pointsTarget || 500).toLocaleString()}` },
    ]
    : [
      { k: 'How you earn', v: 'One stamp per visit' },
      { k: 'Your progress', v: `${item.state.filled || 0} of ${item.scheme.stampsRequired || 6}` },
      { k: 'Reward', v: `${item.scheme.rewardLabel} at ${item.scheme.stampsRequired || 6}` },
    ];
  return ReactDOM.createPortal(
    <div className="wallet-modal-overlay" onClick={onClose}>
      <div className="wallet-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`${item.salon.name} loyalty card`}>
        <header className="wallet-modal-header">
          <h2 className="wallet-modal-title">{item.salon.name}</h2>
          <CloseButton onClick={onClose} />
        </header>
        <div className="wallet-modal-body">
          <div className="wallet-detail-art">
            <ResponsiveLoyaltyCard design={item.design} scheme={item.scheme} state={item.state} salonName={item.salon.name} />
          </div>
          {isReady(item) ? (
            <div className="loy-reward-banner">🎉 Your reward is ready to use on your next visit.</div>
          ) : null}
          <div className="wallet-detail-rows">
            {rows.map((r) => (
              <div key={r.k} className="wallet-detail-row"><span>{r.k}</span><strong>{r.v}</strong></div>
            ))}
          </div>
        </div>
        {onBook ? (
          <div className="wallet-modal-footer">
            <button type="button" className="wallet-cta" onClick={() => onBook(item)}>Book at {item.salon.name}</button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

// LoyaltyWallet — the signed-in customer's loyalty cards across salons, in a
// wallet-style browser with search. Lives at /dashboard/loyalty.
const LoyaltyWallet = ({ history, embedded = false }) => {
  const { loading, items: allItems } = useWallet();
  const items = useMemo(() => allItems.filter((i) => i.kind === 'loyalty'), [allItems]);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const main = document.querySelector('.dashboard-main');
    if (main) main.scrollTop = 0;
  }, []);

  const cards = items;
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => (c.salon.name || '').toLowerCase().includes(q));
  }, [cards, query]);

  const book = (item) => { history.push(`/book/${item.salon.slug || item.salonId}`); };

  return (
    <div className={embedded ? 'wallet-page loyalty-wallet-page' : 'dashboard-fade-in wallet-page loyalty-wallet-page'}>
      {!embedded && (
        <div className="dashboard-page-header">
          <button type="button" className="dashboard-back-button" onClick={() => history.push('/dashboard/account')} aria-label="Back to account">
            <BackArrowIcon />
          </button>
          <div className="dashboard-page-header-text">
            <h1 className="dashboard-page-title">Loyalty</h1>
            <p className="dashboard-page-subtitle">Your stamp cards and points across salons</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="wallet-loading"><div className="wallet-spinner" aria-hidden /></div>
      ) : cards.length === 0 ? (
        <div className="wallet-empty">
          <div className="wallet-empty-icon"><StampIcon /></div>
          <h2 className="wallet-empty-title">No loyalty cards yet</h2>
          <p className="wallet-empty-text">Collect stamps and points at salons you visit, and your cards will live here, ready to show at the till.</p>
          <button type="button" className="wallet-cta wallet-empty-cta" onClick={() => history.push('/')}>Browse salons</button>
        </div>
      ) : (
        <>
          <div className="loy-search">
            <SearchIcon />
            <input
              type="text"
              className="loy-search-input"
              placeholder="Search your salons"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {visible.length === 0 ? (
            <p className="loy-no-results">No cards match “{query}”.</p>
          ) : (
            <div className="loy-list">
              {visible.map((item) => (
                <button key={item.id} type="button" className="loy-item" onClick={() => setActive(item)}>
                  <div className="loy-item-card">
                    <ResponsiveLoyaltyCard design={item.design} scheme={item.scheme} state={item.state} salonName={item.salon.name} />
                  </div>
                  <div className="loy-item-row">
                    <span className="loy-item-salon">{item.salon.name}</span>
                    <span className={`loy-item-progress${item.rewardReady ? ' is-ready' : ''}`}>{progressText(item)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {active && (
        <LoyaltyDetail item={active} onClose={() => setActive(null)} onBook={book} />
      )}
    </div>
  );
};

export default LoyaltyWallet;
